import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormHelperText,
  Typography,
  Paper,
  TextField,
} from "@mui/material";
import axios from "axios";

import { RetryImage } from "../../../components/RetryImage";
import { SelectComponent } from "../../../components/Select";
import { PhoneInput } from "../../../components/PhoneInput";
import { usStates, zipRegex } from "../../../constants/Constant";
import {
  createCompany,
  getCompany,
  updateCompany,
} from "../../../apis/company";
import { CompanyProps, TabProps } from "../../../constants/Interface";
import { useGlobalSnackBar } from "../../SnackbarContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const InfoTab: React.FC<TabProps> = ({ value }) => {
  const [company, setCompany] = useState<CompanyProps | null>(null);
  const [logo, setLogo] = useState<string | ArrayBuffer | null>(null);
  const [zip, setZip] = useState<string>("");
  const [isZipValid, setIsZipValid] = useState<boolean>(true);
  const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

  const handleZipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setZip(newValue);
    setIsZipValid(zipRegex.test(newValue));
  };

  const { showSnackBar } = useGlobalSnackBar();

  const fetchCompany = async () => {
    try {
      const companyData = await getCompany();
      setCompany(companyData?.[0]);
      setLogo(companyData?.[0]?.company_logo);
    } catch (error) {
      console.error("Failed to fetch company", error);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (currentUser?.permission !== "Administrator") {
      showSnackBar("Invalid operation: Only Administrators can make this change.", "error");
      return;
    }

    const formData = new FormData(event.target);
    const formValues: any = Object.fromEntries(formData.entries());
    formValues["company_logo"] = logo;

    if (!isZipValid) {
      showSnackBar("Form is invalid! Please check the fields.", "error");
      return;
    }

    if (!company?.ID) {
      await createCompany(formValues);
      showSnackBar("Created!");
    } else {
      await updateCompany({ ...formValues, ID: company.ID });
      showSnackBar("Updated!");
    }
    fetchCompany();
  };

  const handleUploadClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("img", file);

    axios
      .post(process.env.REACT_APP_UPLOAD_URL || "", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + localStorage.getItem("admin_access_token"),
        },
      })
      .then((response) => {
        setLogo(response.data.filename);
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
      });
  };

  const isAdmin = currentUser?.permission === "Administrator";

  return (
    <CustomTabPanel value={value} index={2}>
      <Box
        sx={{ maxWidth: 640, mx: "auto" }}
        component="form"
        onSubmit={handleSubmit}
      >
        {/* Logo */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2, textAlign: "center" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Company Logo
          </Typography>
          <RetryImage
            src={
              typeof logo === "string"
                ? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${logo}`
                : undefined
            }
            sx={{
              width: 160,
              height: 160,
              m: "0 auto",
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
          <input
            accept="image/*"
            id="contained-button-file"
            type="file"
            onChange={handleUploadClick}
            style={{ display: "none" }}
          />
          <label htmlFor="contained-button-file">
            <Button
              variant="outlined"
              component="span"
              size="small"
              sx={{ mt: 2 }}
              disabled={!isAdmin}
            >
              {logo ? "Change Logo" : "Upload Logo"}
            </Button>
          </label>
        </Paper>

        {/* Company Details */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Company Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Company Name"
              name="company_name"
              defaultValue={company?.company_name}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <PhoneInput
              label="Phone"
              name="phone"
              defaultValue={company?.phone || ""}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Paper>

        {/* Address */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: "#E2E8F0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#0B1929" }}>
            Address
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Address"
              name="address"
              defaultValue={company?.address}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="City"
                name="city"
                defaultValue={company?.city}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ minWidth: 120 }}>
                <SelectComponent
                  defaultValue={company?.state}
                  name="state"
                  menuItems={usStates}
                  label="State"
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Zip"
                name="zip"
                defaultValue={company?.zip}
                value={zip || company?.zip || ""}
                onChange={handleZipChange}
                error={!isZipValid}
                helperText={!isZipValid ? "Format: 12345 or 12345-6789" : ""}
                size="small"
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Country"
                name="country"
                defaultValue={company?.country}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </Paper>

        <Box sx={{ textAlign: "right" }}>
          <Button sx={{ px: 4 }} variant="contained" type="submit" disabled={!isAdmin}>
            Save Info
          </Button>
        </Box>
      </Box>
    </CustomTabPanel>
  );
};
